import { DeviceInfo, DeviceInfoOSName } from '../auth/common/configuration/DeviceInfoTypes'
import { getSHA256 } from '../util/crypto'
import { DefaultDeviceInfo } from './DefaultDeviceInfo'
import { uuid } from '../util/uuid'

export const AUTH_STORE_KEY_PREFIX = 'com.turner.top-2'
const DEVICE_ID_STORE_KEY = `${AUTH_STORE_KEY_PREFIX}.deviceId`

const isNil = (val: unknown): val is null | undefined => {
  return val === null || val === undefined
}

const omitBy = <T extends Record<string, any>>(obj: T, predicate: (val: any) => boolean): Partial<T> => {
  const result: Partial<T> = {}
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      if (!predicate(obj[key])) {
        result[key] = obj[key]
      }
    }
  }
  return result
}

interface AuthConfigLite {
  brand: string
  deviceInfo?: Partial<DeviceInfo>
}

/**
 * Service for managing device information and identification.
 *
 * This service handles:
 * 1. Hard-coded default properties
 * 2. Browser/screen detection via DefaultDeviceInfo
 * 3. User-provided configuration overrides
 *
 * All methods are synchronous after calling init() once during setup.
 */
export class DeviceInfoService {
  private _cachedDeviceId?: string
  private _cachedHashedDeviceId?: string
  private _cachedEncodedDeviceInfo?: string
  private _initPromise?: Promise<void>

  constructor(private readonly authConfig: AuthConfigLite) {}

  /**
   * Initialize all device info data.
   * This should be called once during setRequestor() or similar initialization.
   *
   * After this completes, all synchronous getters will have accurate data.
   * Safe to call multiple times - will only initialize once.
   *
   * @returns Promise that resolves when initialization is complete
   */
  public async init(): Promise<void> {
    if (!this._initPromise) {
      this._initPromise = (async () => {
        await this._setDeviceId()
        this._cachedEncodedDeviceInfo = this._encodeDeviceInfo()
      })()
    }

    return this._initPromise
  }

  /**
   * Encode device info as base64 string.
   * Returns empty string on error.
   */
  private _encodeDeviceInfo(): string {
    try {
      const deviceInfo = this.getDeviceInfo()
      const stringified = JSON.stringify(deviceInfo)
      return btoa(stringified)
    } catch (error) {
      console.warn(`Failed to encode device info: ${error}`)
      return ''
    }
  }

  /**
   * Initialize device ID from localStorage or generate a new one.
   */
  private async _setDeviceId(): Promise<void> {
    let deviceId: string | undefined

    try {
      const localDeviceId = localStorage.getItem(DEVICE_ID_STORE_KEY)
      if (localDeviceId) {
        deviceId = localDeviceId
      }
    } catch {
      // unable to fetch stored deviceId
    }

    if (!deviceId) {
      deviceId = uuid()
      if (deviceId) {
        try {
          localStorage.setItem(DEVICE_ID_STORE_KEY, deviceId)
        } catch {
          // unable to store, deviceId will be unique each time
        }
      }
    }

    this._cachedDeviceId = deviceId
    // btoa is safe for this operation because sha256 only produces hexadecimal.
    this._cachedHashedDeviceId = deviceId ? btoa(getSHA256(deviceId)) : undefined
  }

  /**
   * Build a complete DeviceInfo object (synchronously) by merging data from multiple sources.
   *
   * @returns A complete DeviceInfo object with all required and optional properties
   */
  public getDeviceInfo(): DeviceInfo {
    // Layer 1: Base defaults
    const baseDeviceInfo: DeviceInfo = {
      applicationId: `${this.authConfig.brand}:auth-clientless-web-lite`,
      model: 'Browser',
      osName: this._detectOSName(),
      osVersion: 'unknown',
      version: 'unknown',
      connectionType: 'unknown',
    }

    // Layer 2: Browser detection
    const browserDeviceInfo = DefaultDeviceInfo.get()

    // Layer 3: User overrides (highest priority)
    return {
      ...baseDeviceInfo,
      ...omitBy(browserDeviceInfo, isNil),
      ...omitBy(this.authConfig.deviceInfo || {}, isNil),
    }
  }

  /**
   * Detect OS name from user agent.
   */
  private _detectOSName(): DeviceInfoOSName {
    if (typeof navigator === 'undefined' || !navigator.userAgent) {
      return 'unknown' as DeviceInfoOSName
    }

    const ua = navigator.userAgent.toLowerCase()

    if (ua.includes('win')) return 'Windows'
    if (ua.includes('mac')) return 'Mac OS'
    if (ua.includes('linux')) return 'Linux'
    if (ua.includes('android')) return 'Android'
    if (ua.includes('iphone') || ua.includes('ipad')) return 'iOS'
    if (ua.includes('cros')) return 'Chrome OS'

    return 'unknown' as DeviceInfoOSName
  }

  /**
   * Get the cached device ID. Must call init() before using this method.
   *
   * @returns The device ID
   * @throws Error if init() has not been called
   */
  public getDeviceID(): string {
    if (!this._cachedDeviceId) {
      throw new Error('DeviceInfoService.init() must be called before getDeviceID()')
    }
    return this._cachedDeviceId
  }

  /**
   * Get the SHA256 hash of the device ID.
   * This is used by Adobe Pass V2 APIs for device identification.
   *
   * @returns The SHA256 hash of the device ID
   * @throws Error if init() has not been called
   */
  public getHashedDeviceId(): string {
    if (!this._cachedHashedDeviceId) {
      throw new Error('DeviceInfoService.init() must be called before getHashedDeviceId()')
    }
    return this._cachedHashedDeviceId
  }

  /**
   * Get the device info as a base64 string.
   * This is used for the 'X-Device-Info' header in Adobe Pass API requests.
   *
   * The device info is JSON stringified and converted to base64.
   * Must call init() before using this method.
   *
   * @returns The base64 device info string
   * @throws Error if init() has not been called
   */
  public getEncodedDeviceInfo(): string {
    if (!this._cachedEncodedDeviceInfo) {
      throw new Error('DeviceInfoService.init() must be called before getEncodedDeviceInfo()')
    }
    return this._cachedEncodedDeviceInfo
  }
}

